/**
 * Search Results HTML renderer for mcp-ui.
 *
 * Renders semantic code search results with expandable detail sections.
 * Self-contained — all styles inline, no external dependencies.
 */

import { BASE_STYLES } from './styles.js';

interface SearchResultSummary {
  type?: string | undefined;
  name?: string | undefined;
  signature?: string | undefined;
  purpose?: string | undefined;
  location?: string | undefined;
  relevanceReason?: string | undefined;
  storeName?: string | undefined;
  repoRoot?: string | undefined;
}

interface SearchResultContext {
  interfaces?: readonly string[] | undefined;
  keyImports?: readonly string[] | undefined;
  relatedConcepts?: readonly string[] | undefined;
  usage?: { calledBy: number; calls: number } | undefined;
}

interface SearchResultFull {
  completeCode?: string | undefined;
  relatedCode?: readonly { file: string; summary: string; relationship: string }[] | undefined;
  documentation?: string | undefined;
}

export interface SearchResultData {
  id: string;
  score: number;
  summary: SearchResultSummary;
  context?: SearchResultContext | undefined;
  full?: SearchResultFull | undefined;
}

export interface SearchMetadata {
  timeMs: number;
  confidence?: string | undefined;
  mode: string;
  stores: Record<string, { type: string; path?: string | undefined; url?: string | undefined }>;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getScoreColor(score: number): string {
  if (score >= 0.7) return 'var(--score-high)';
  if (score >= 0.4) return 'var(--score-medium)';
  return 'var(--score-low)';
}

function getScoreClass(score: number): string {
  if (score >= 0.7) return 'high';
  if (score >= 0.4) return 'medium';
  return 'low';
}

function getConfidenceClass(confidence: string): string {
  switch (confidence) {
    case 'high':
      return 'confidence-high';
    case 'medium':
      return 'confidence-medium';
    case 'low':
      return 'confidence-low';
    default:
      return 'confidence-medium';
  }
}

function getTypeIcon(type: string): string {
  switch (type) {
    case 'function':
      return '<span class="type-icon type-fn">fn</span>';
    case 'class':
      return '<span class="type-icon type-class">C</span>';
    case 'interface':
      return '<span class="type-icon type-iface">I</span>';
    case 'type':
      return '<span class="type-icon type-type">T</span>';
    case 'const':
      return '<span class="type-icon type-const">K</span>';
    case 'documentation':
      return '<span class="type-icon type-doc">D</span>';
    case 'example':
      return '<span class="type-icon type-example">E</span>';
    default:
      return '<span class="type-icon type-default">?</span>';
  }
}

function renderContextSection(context: SearchResultContext): string {
  const sections: string[] = [];

  if (context.interfaces !== undefined && context.interfaces.length > 0) {
    sections.push(`
      <div class="context-group">
        <div class="section-label">Interfaces</div>
        <ul class="pill-list">
          ${context.interfaces.map((i) => `<li class="pill">${escapeHtml(i)}</li>`).join('')}
        </ul>
      </div>
    `);
  }

  if (context.keyImports !== undefined && context.keyImports.length > 0) {
    sections.push(`
      <div class="context-group">
        <div class="section-label">Key Imports</div>
        <ul class="pill-list">
          ${context.keyImports.map((i) => `<li class="pill">${escapeHtml(i)}</li>`).join('')}
        </ul>
      </div>
    `);
  }

  if (context.relatedConcepts !== undefined && context.relatedConcepts.length > 0) {
    sections.push(`
      <div class="context-group">
        <div class="section-label">Related Concepts</div>
        <ul class="pill-list">
          ${context.relatedConcepts.map((c) => `<li class="pill">${escapeHtml(c)}</li>`).join('')}
        </ul>
      </div>
    `);
  }

  if (context.usage !== undefined) {
    sections.push(`
      <div class="context-group">
        <div class="section-label">Usage</div>
        <div class="usage-stats">
          <span class="usage-stat">
            <span class="usage-value">${String(context.usage.calledBy)}</span>
            <span class="usage-label">called by</span>
          </span>
          <span class="usage-stat">
            <span class="usage-value">${String(context.usage.calls)}</span>
            <span class="usage-label">calls</span>
          </span>
        </div>
      </div>
    `);
  }

  return sections.join('');
}

function renderFullSection(full: SearchResultFull): string {
  const sections: string[] = [];

  if (full.documentation !== undefined && full.documentation.length > 0) {
    sections.push(`
      <div class="full-group">
        <div class="section-label">Documentation</div>
        <div class="documentation-text">${escapeHtml(full.documentation)}</div>
      </div>
    `);
  }

  if (full.completeCode !== undefined && full.completeCode.length > 0) {
    sections.push(`
      <div class="full-group">
        <div class="section-label">Source Code</div>
        <pre class="code-block"><code>${escapeHtml(full.completeCode)}</code></pre>
      </div>
    `);
  }

  if (full.relatedCode !== undefined && full.relatedCode.length > 0) {
    sections.push(`
      <div class="full-group">
        <div class="section-label">Related Code</div>
        <div class="related-code-list">
          ${full.relatedCode
            .map(
              (rc) => `
            <div class="related-code-item">
              <div class="related-code-file mono">${escapeHtml(rc.file)}</div>
              <div class="related-code-summary">${escapeHtml(rc.summary)}</div>
              <div class="related-code-relationship">${escapeHtml(rc.relationship)}</div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    `);
  }

  return sections.join('');
}

function renderResultCard(result: SearchResultData, index: number): string {
  const { summary, context, full } = result;
  const scorePercent = Math.round(result.score * 100);
  const scoreColor = getScoreColor(result.score);
  const scoreClass = getScoreClass(result.score);

  const hasContext =
    context !== undefined &&
    ((context.interfaces !== undefined && context.interfaces.length > 0) ||
      (context.keyImports !== undefined && context.keyImports.length > 0) ||
      (context.relatedConcepts !== undefined && context.relatedConcepts.length > 0) ||
      context.usage !== undefined);

  const hasFull =
    full !== undefined &&
    ((full.completeCode !== undefined && full.completeCode.length > 0) ||
      (full.relatedCode !== undefined && full.relatedCode.length > 0) ||
      (full.documentation !== undefined && full.documentation.length > 0));

  const locationParts = summary.location?.split(':') ?? [];
  const fileName = locationParts[0] ?? '';
  const lineNum = locationParts[1] ?? '';

  return `
    <div class="result-card">
      <div class="result-header">
        <div class="result-rank">${String(index + 1)}</div>
        <div class="result-main">
          <div class="result-title-row">
            ${summary.type !== undefined ? getTypeIcon(summary.type) : ''}
            <span class="result-name">${escapeHtml(summary.name ?? 'Unknown')}</span>
            <div class="score-bar-container">
              <div class="score-bar-track">
                <div class="score-bar-fill" style="width: ${String(scorePercent)}%; background: ${scoreColor}"></div>
              </div>
              <span class="score-label score-${scoreClass}">${String(scorePercent)}%</span>
            </div>
          </div>
          ${
            summary.signature !== undefined
              ? `<div class="result-signature mono">${escapeHtml(summary.signature)}</div>`
              : ''
          }
          ${
            summary.purpose !== undefined
              ? `<p class="result-purpose">${escapeHtml(summary.purpose)}</p>`
              : ''
          }
          <div class="result-meta meta-row">
            ${
              fileName.length > 0
                ? `<span class="result-location mono" title="${escapeHtml(summary.location ?? '')}">
                    ${escapeHtml(fileName)}${lineNum.length > 0 ? `<span class="line-number">:${escapeHtml(lineNum)}</span>` : ''}
                   </span>`
                : ''
            }
            ${
              summary.storeName !== undefined
                ? `<span class="meta-separator"></span>
                   <span class="result-store">${escapeHtml(summary.storeName)}</span>`
                : ''
            }
          </div>
          ${
            summary.relevanceReason !== undefined
              ? `<div class="result-relevance">${escapeHtml(summary.relevanceReason)}</div>`
              : ''
          }
        </div>
      </div>
      ${
        hasContext || hasFull
          ? `<details>
              <summary>${hasContext && hasFull ? 'Context & Source Code' : hasContext ? 'Context' : 'Source Code'}</summary>
              <div class="details-content">
                ${hasContext ? renderContextSection(context) : ''}
                ${hasFull ? renderFullSection(full) : ''}
              </div>
            </details>`
          : ''
      }
    </div>
  `;
}

function renderStoresList(
  stores: Record<string, { type: string; path?: string | undefined; url?: string | undefined }>
): string {
  const entries = Object.entries(stores);
  if (entries.length === 0) return '';

  return entries
    .map(([name, info]) => {
      const badgeClass =
        info.type === 'repo' ? 'badge-repo' : info.type === 'file' ? 'badge-file' : 'badge-web';
      const location = info.path ?? info.url ?? '';
      return `
      <span class="store-chip">
        <span class="badge ${badgeClass}" style="font-size: 10px; padding: 1px 6px;">${escapeHtml(info.type)}</span>
        <span class="store-chip-name">${escapeHtml(name)}</span>
        ${location.length > 0 ? `<span class="store-chip-location mono truncate" title="${escapeHtml(location)}">${escapeHtml(location)}</span>` : ''}
      </span>
    `;
    })
    .join('');
}

export function renderSearchResults(
  query: string,
  results: SearchResultData[],
  metadata: SearchMetadata
): string {
  const confidenceHtml =
    metadata.confidence !== undefined
      ? `<span class="confidence-badge ${getConfidenceClass(metadata.confidence)}">${escapeHtml(metadata.confidence)} confidence</span>`
      : '';

  const storesHtml = renderStoresList(metadata.stores);

  const contentHtml =
    results.length === 0
      ? `<div class="empty-state">
           <div class="empty-state-title">No results found</div>
           <p>Try broadening your search query or checking different stores.</p>
         </div>`
      : `<div class="results-list">
           ${results.map((result, index) => renderResultCard(result, index)).join('')}
         </div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Search Results</title>
  <style>
    ${BASE_STYLES}

    /* Search-specific styles */
    .search-header {
      background: var(--bg-secondary);
      border: 1px solid var(--border-primary);
      border-radius: var(--radius-lg);
      padding: var(--space-lg);
      margin-bottom: var(--space-xl);
    }

    .search-query {
      font-size: var(--font-size-lg);
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: var(--space-md);
      display: flex;
      align-items: flex-start;
      gap: var(--space-sm);
    }

    .search-query-icon {
      color: var(--text-tertiary);
      flex-shrink: 0;
      margin-top: 2px;
    }

    .search-query-text {
      color: var(--accent-blue);
      word-break: break-word;
    }

    .search-meta {
      display: flex;
      align-items: center;
      gap: var(--space-md);
      flex-wrap: wrap;
      font-size: var(--font-size-sm);
      color: var(--text-secondary);
    }

    .search-meta-item {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
    }

    .confidence-badge {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: 9999px;
      font-size: var(--font-size-xs);
      font-weight: 500;
      text-transform: capitalize;
    }

    .confidence-high {
      background: var(--confidence-high-bg);
      color: var(--confidence-high-text);
    }

    .confidence-medium {
      background: var(--confidence-medium-bg);
      color: var(--confidence-medium-text);
    }

    .confidence-low {
      background: var(--confidence-low-bg);
      color: var(--confidence-low-text);
    }

    .stores-searched {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-sm);
      margin-top: var(--space-md);
      padding-top: var(--space-md);
      border-top: 1px solid var(--border-secondary);
    }

    .stores-searched-label {
      font-size: var(--font-size-xs);
      color: var(--text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      width: 100%;
      margin-bottom: var(--space-xs);
    }

    .store-chip {
      display: inline-flex;
      align-items: center;
      gap: var(--space-xs);
      padding: var(--space-xs) var(--space-sm);
      background: var(--bg-tertiary);
      border: 1px solid var(--border-secondary);
      border-radius: var(--radius-md);
      font-size: var(--font-size-sm);
    }

    .store-chip-name {
      color: var(--text-primary);
      font-weight: 500;
    }

    .store-chip-location {
      color: var(--text-tertiary);
      font-size: var(--font-size-xs);
      max-width: 200px;
    }

    /* Results list */
    .results-list {
      display: flex;
      flex-direction: column;
      gap: var(--space-md);
    }

    .result-card {
      background: var(--bg-card);
      border: 1px solid var(--border-primary);
      border-radius: var(--radius-lg);
      overflow: hidden;
      transition: border-color var(--transition-normal);
    }

    .result-card:hover {
      border-color: rgba(88, 166, 255, 0.3);
    }

    .result-header {
      display: flex;
      gap: var(--space-md);
      padding: var(--space-lg);
    }

    .result-rank {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--bg-tertiary);
      border: 1px solid var(--border-secondary);
      border-radius: var(--radius-sm);
      font-size: var(--font-size-sm);
      font-weight: 600;
      color: var(--text-tertiary);
      font-family: var(--font-mono);
    }

    .result-main {
      flex: 1;
      min-width: 0;
    }

    .result-title-row {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
      margin-bottom: var(--space-xs);
    }

    .type-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: var(--radius-sm);
      font-size: 11px;
      font-weight: 700;
      font-family: var(--font-mono);
      flex-shrink: 0;
    }

    .type-fn       { background: rgba(88, 166, 255, 0.15); color: var(--accent-blue); }
    .type-class    { background: rgba(210, 153, 34, 0.15); color: var(--accent-orange); }
    .type-iface    { background: rgba(57, 210, 192, 0.15); color: var(--accent-cyan); }
    .type-type     { background: rgba(188, 140, 255, 0.15); color: var(--accent-purple); }
    .type-const    { background: rgba(63, 185, 80, 0.15); color: var(--accent-green); }
    .type-doc      { background: rgba(139, 148, 158, 0.15); color: var(--text-secondary); }
    .type-example  { background: rgba(247, 120, 186, 0.15); color: var(--accent-pink); }
    .type-default  { background: rgba(139, 148, 158, 0.1); color: var(--text-tertiary); }

    .result-name {
      font-size: var(--font-size-md);
      font-weight: 600;
      color: var(--text-primary);
      font-family: var(--font-mono);
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .result-signature {
      color: var(--text-secondary);
      margin-bottom: var(--space-sm);
      font-size: var(--font-size-sm);
      line-height: 1.4;
      overflow-x: auto;
      white-space: pre-wrap;
      word-break: break-all;
    }

    .result-purpose {
      color: var(--text-secondary);
      font-size: var(--font-size-sm);
      margin-bottom: var(--space-sm);
      line-height: 1.5;
    }

    .result-meta {
      margin-bottom: var(--space-xs);
    }

    .result-location {
      color: var(--text-tertiary);
      font-size: var(--font-size-sm);
    }

    .line-number {
      color: var(--accent-blue);
    }

    .result-store {
      font-size: var(--font-size-sm);
      color: var(--text-tertiary);
    }

    .result-relevance {
      font-size: var(--font-size-sm);
      color: var(--text-secondary);
      font-style: italic;
      margin-top: var(--space-xs);
      padding-left: var(--space-md);
      border-left: 2px solid var(--border-secondary);
    }

    .score-high { color: var(--score-high); }
    .score-medium { color: var(--score-medium); }
    .score-low { color: var(--score-low); }

    /* Context & Full sections inside details */
    .result-card details {
      margin: 0;
      border-top: 1px solid var(--border-secondary);
    }

    .result-card details summary {
      padding: var(--space-sm) var(--space-lg);
      font-weight: 500;
    }

    .result-card .details-content {
      padding: 0 var(--space-lg) var(--space-lg);
    }

    .context-group,
    .full-group {
      margin-bottom: var(--space-md);
    }

    .context-group:last-child,
    .full-group:last-child {
      margin-bottom: 0;
    }

    .usage-stats {
      display: flex;
      gap: var(--space-lg);
    }

    .usage-stat {
      display: flex;
      align-items: baseline;
      gap: var(--space-xs);
    }

    .usage-value {
      font-size: var(--font-size-lg);
      font-weight: 700;
      font-family: var(--font-mono);
      color: var(--text-primary);
    }

    .usage-label {
      font-size: var(--font-size-xs);
      color: var(--text-tertiary);
    }

    .documentation-text {
      font-size: var(--font-size-sm);
      color: var(--text-secondary);
      line-height: 1.6;
      white-space: pre-wrap;
    }

    .related-code-list {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
    }

    .related-code-item {
      padding: var(--space-sm) var(--space-md);
      background: var(--bg-inset);
      border: 1px solid var(--border-secondary);
      border-radius: var(--radius-md);
    }

    .related-code-file {
      font-size: var(--font-size-sm);
      color: var(--accent-blue);
      margin-bottom: 2px;
    }

    .related-code-summary {
      font-size: var(--font-size-sm);
      color: var(--text-primary);
    }

    .related-code-relationship {
      font-size: var(--font-size-xs);
      color: var(--text-tertiary);
      font-style: italic;
      margin-top: 2px;
    }

    /* Code block inside result cards — tighter fit */
    .result-card .code-block {
      max-height: 400px;
      overflow-y: auto;
      font-size: var(--font-size-sm);
    }

    /* Scrollbar styling for code blocks */
    .code-block::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    .code-block::-webkit-scrollbar-track {
      background: transparent;
    }

    .code-block::-webkit-scrollbar-thumb {
      background: var(--border-primary);
      border-radius: 3px;
    }

    .code-block::-webkit-scrollbar-thumb:hover {
      background: var(--text-tertiary);
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="search-header">
      <div class="search-query">
        <span class="search-query-icon">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor"><path d="M10.68 11.74a6 6 0 01-7.922-8.982 6 6 0 018.982 7.922l3.04 3.04a.749.749 0 01-.326 1.275.749.749 0 01-.734-.215l-3.04-3.04zM11.5 7a4.499 4.499 0 10-8.997 0A4.499 4.499 0 0011.5 7z"/></svg>
        </span>
        <span class="search-query-text">${escapeHtml(query)}</span>
      </div>
      <div class="search-meta">
        <span class="search-meta-item">
          <strong>${String(results.length)}</strong>&nbsp;result${results.length !== 1 ? 's' : ''}
        </span>
        <span class="meta-separator"></span>
        <span class="search-meta-item">${String(metadata.timeMs)}ms</span>
        <span class="meta-separator"></span>
        <span class="search-meta-item">${escapeHtml(metadata.mode)} mode</span>
        ${confidenceHtml.length > 0 ? `<span class="meta-separator"></span>${confidenceHtml}` : ''}
      </div>
      ${
        storesHtml.length > 0
          ? `<div class="stores-searched">
               <span class="stores-searched-label">Stores Searched</span>
               ${storesHtml}
             </div>`
          : ''
      }
    </div>
    ${contentHtml}
  </div>
</body>
</html>`;
}
